import { Linking } from 'react-native';
import audioService from '../services/AudioService';
import { playErrorFeedback, playSuccessFeedback, showsFailImage, showsSuccessImage } from '../utils/scanFeedback';
import * as RootNavigation from '../components/root_navigation';
import {
  setOrderComplete,
  setOrderCompleteAction,
  setPostScanPipeline,
  setScanPackScanError,
  setScanPackScanSuccess,
  setBoxChampState,
  setScanPackBarcodeInput,
  getStateValues,
  addLocalLog,
  navigateBack
} from './scanpackScanningSetters';
import {
  getGuidedView,
  getGuidedHandoff,
  isGuidedOrderPacked
} from '../components/ScanpackScanning/utils/boxChampGuidedSelectors';
import { HANDOFF_IDLE } from '../components/scanpack/boxChampHandoff';
import { getSuccessTimeout } from '../utils/orderHelpers';
import { resetSubmitLogThrottle, submitQueuedLogsPromptly, submitQueuedLogsBounded } from './submitLogThrottle';
import { SubmitLog } from './saveLogAction';
import { FullOrderScan, ResetSearchOrderData } from './scanpackAction';
import { emailCustomer } from './orderActions';
import { handleShippingEasyShipment } from './shipmentActions';
import { handleMultiBoxShippingLabel, printCompletedOrderSlips } from './multiBoxActions';
import { getReprintPrintType, queueReprintPrompt } from '../components/reprint/reprintUtils';
import { reset } from './scanpackScanningActions';
import { getLiveStatsData } from '../helpers/scanPackFunctions';
import {
  shouldShowPackingCam,
  getPostScanStepConfig,
  validateTrackingPrefix,
  openShopifyPopup,
  openOrderCupPopup,
  getShippingLabelAction
} from '../components/ScanpackScanning/utils/postScanHandlers';
import { getPostScanSteps } from '../utils/postScanning/postScanConfig';
import { runPostScanFlow } from '../utils/postScanning/postScanRunner';
import { createPostScanDriver } from '../utils/postScanning/createPostScanDriver';
import { initialPostScanPipeline } from '../reducers/scanpackScanningInitialState';
import { getTrackingLog } from '../components/ScanpackScanning/utils/scanningUtils';
import { validateConfirmationCode } from '../utils/sharedValidationUtils';
import { saveLogToStorage } from '../utils/logStorageUtils';

// ScanPack keeps no loading flag during post-scanning, so this stays a no-op.
const setLoading = () => {};

const getApiOrder = state => state?.scanpack?.searchOrder?.data?.order?.[0];

const PROMPT_STEP_TYPES = {
  PostScanRecordTracking: 'Record',
  PostScanVerifyShipping: 'Verify'
};

function scanPackPostScanConfig(allSteps) {
  return {
    setLoading,

    // Drives the existing pipeline-based hand-off (usePackingCamNavigation)
    // rather than the shared route-param one. Declining keeps the ScanPack gate.
    openPackingCam: ({ order, remainingModules }) => (dispatch, getState) => {
      const { scanPackSettings } = getStateValues(getState);
      if (!shouldShowPackingCam(scanPackSettings, order?.storeinfo)) return false;
      dispatch(setPostScanPipeline({
        ...initialPostScanPipeline,
        active: true,
        currentStep: 'PackingCam',
        steps: allSteps,
        remainingSteps: remainingModules
      }));
      return true;
    },

    showPrompt: (dispatch, { promptType, order, remainingModules }) => {
      const optionType = PROMPT_STEP_TYPES[promptType];
      const config = getPostScanStepConfig(optionType, order);
      if (!config) {
        dispatch(finalizeOrder());
        return;
      }
      const stepIndex = allSteps.length - remainingModules.length - 1;
      dispatch(setPostScanPipeline({
        ...initialPostScanPipeline,
        active: true,
        currentStep: optionType,
        isSecondOption: stepIndex > 0,
        trackingInput: '',
        verifyNoTracking: config.verifyNoTracking,
        fieldLabel: config.fieldLabel,
        orderNumber: order?.increment_id || '',
        messageDetail: config.messageDetail,
        scanSecondTime: false,
        trackingLogState: '',
        steps: allSteps,
        remainingSteps: remainingModules
      }));
    },

    shippingLabel: {
      // finalizeOrder reads the label flag off the search-order payload, not off
      // currentOrder, so this gate matches it.
      isApplicable: state => Boolean(getApiOrder(state)?.print_ss_label),
      navigate: (dispatch, getState, { order, orderId, remainingModules }) => {
        dispatch(setPostScanPipeline({
          ...getState().scanPackScanning.postScanPipeline,
          active: true,
          currentStep: 'ShippingLabel',
          remainingSteps: remainingModules
        }));
        RootNavigation.navigate('ShippingLabel', {
          basicinfo: order,
          isolatedComponent: true,
          returnToScanpackScanning: true,
          currentOrderId: orderId,
          setting: getState().user?.bothSettings?.data?.data,
          userRole: getState().user?.userInfo,
          directPrintingSetting:
            getState().user?.bothSettings?.data?.data?.general_setting?.print_ss_shipping_labels,
          postScanningContinue: true,
          postScanningModulesRemaining: remainingModules
        });
      }
    },

    // v1 PackingSlip and Barcode are no-ops here: the backend prints them when the
    // order completes. v2 module names fall through to the backend.
    runStep: ({ moduleName }) => () => moduleName === 'PackingSlip' || moduleName === 'Barcode',

    finalize: () => finalizeOrder()
  };
}

function runPostScanSteps(allSteps, pending) {
  return async (dispatch, getState) => {
    const { currentOrder } = getStateValues(getState);
    await dispatch(
      runPostScanFlow(createPostScanDriver(scanPackPostScanConfig(allSteps)), {
        order: currentOrder,
        orderId: currentOrder?.id,
        modules: pending,
        toteInput: null
      })
    );
  };
}

// Continues the pipeline from whatever the pipeline state says is left. Used by
// both resume paths (tracking prompt submit, packing cam return).
export function resumePostScanSteps() {
  return async (dispatch, getState) => {
    const pipeline = getState().scanPackScanning.postScanPipeline;
    await dispatch(runPostScanSteps(pipeline.steps || [], pipeline.remainingSteps || []));
  };
}

export function holdGuidedCompletion() {
  return (dispatch, getState) => {
    const state = getState();
    const guided = state.scanPackScanning.boxChamp || {};
    if (guided.completionReleased) return false;
    const view = getGuidedView(state);
    if (!view) return false;
    if (
      getGuidedHandoff(state, { ignoreModal: true }).kind === HANDOFF_IDLE &&
      isGuidedOrderPacked(state, view)
    ) {
      return false;
    }
    dispatch(setBoxChampState({ completionPending: true }));
    dispatch(setScanPackBarcodeInput(''));
    playSuccessFeedback(getState);
    const { scanPackSettings } = getStateValues(getState);
    if (showsSuccessImage(scanPackSettings)) {
      dispatch(setScanPackScanSuccess(true, getSuccessTimeout(scanPackSettings)));
    }
    return true;
  };
}

export function handleOrderComplete() {
  return async (dispatch, getState) => {
    if (getState().scanPackScanning.orderComplete) return;

    const { currentOrder, scanPackSettings } = getStateValues(getState);

    if (dispatch(holdGuidedCompletion())) {
      submitQueuedLogsPromptly(dispatch);
      return;
    }

    dispatch(setOrderComplete(true));

    if (scanPackSettings?.order_verification) {
      if (scanPackSettings?.play_order_complete_sound) {
        audioService.playCompleteSound();
      }
      if (scanPackSettings?.show_order_complete_image) {
        const imageTime = (scanPackSettings?.order_complete_image_time || 1) * 400;
        dispatch(setOrderCompleteAction(true, imageTime));
      }
      if (currentOrder?.id) dispatch(FullOrderScan(currentOrder?.id));
      resetSubmitLogThrottle();
      dispatch(SubmitLog('submitLog'));
      setTimeout(() => {
        RootNavigation.navigate('OrderDetail', {
          from: 'ScanPackItem',
          item: currentOrder,
          userData: getState().user?.userInfo
        });
      }, 100);
      return;
    }

    if (getState().scanPackScanning.navigationSource?.from === 'scanToCart') {
      dispatch(finalizeOrder({ skipShippingLabel: true }));
      return;
    }

    const { version, steps } = getPostScanSteps(getState());

    // v1 always ran the packing cam ahead of the options; v2 positions it in the
    // configured list instead.
    const allSteps =
      version === 'v1' && shouldShowPackingCam(scanPackSettings, currentOrder?.storeinfo)
        ? ['packing_cam', ...steps]
        : steps;

    if (!allSteps.length) {
      dispatch(finalizeOrder());
      return;
    }

    await dispatch(runPostScanSteps(allSteps, allSteps));
  };
}

export function releaseGuidedCompletionIfSettled() {
  return (dispatch, getState) => {
    const state = getState();
    const guided = state.scanPackScanning.boxChamp || {};
    if (!guided.completionPending || guided.completionReleased) return;
    const view = getGuidedView(state);
    if (!view) {
      dispatch(setBoxChampState({ completionPending: false }));
      return;
    }
    if (getGuidedHandoff(state, { ignoreModal: true }).kind !== HANDOFF_IDLE) return;
    if (!isGuidedOrderPacked(state, view)) return;
    dispatch(setBoxChampState({ completionPending: false, completionReleased: true }));
    dispatch(handleOrderComplete());
  };
}

/**
 * Handles tracking number input submission for Record/Verify steps.
 */
export function submitTrackingNumber() {
  return async (dispatch, getState) => {
    const { currentOrder, scanPackSettings } = getStateValues(getState);
    const pipeline = getState().scanPackScanning.postScanPipeline;
    const trackingInput = pipeline.trackingInput?.trimStart() || '';

    if (pipeline.currentStep === 'Record') {
      // Empty input not allowed for Record
      if (!trackingInput) {
        if (showsFailImage(scanPackSettings)) dispatch(setScanPackScanError(true, (scanPackSettings?.fail_image_time || 1) * 400));
        playErrorFeedback(getState);
        return;
      }

      // Prefix validation if enabled
      if (scanPackSettings?.tracking_number_validation_enabled) {
        if (!validateTrackingPrefix(trackingInput, scanPackSettings?.tracking_number_validation_prefixes)) {
          if (showsFailImage(scanPackSettings)) dispatch(setScanPackScanError(true, (scanPackSettings?.fail_image_time || 1) * 400));
          playErrorFeedback(getState);
          dispatch(setPostScanPipeline({
            ...pipeline,
            trackingInput: '',
            alertBoxTracking: true
          }));
          return;
        }
      }

      // Create tracking log and chain
      const logData = getTrackingLog(currentOrder, 'scanpack.rfp.default', 'record', trackingInput);
      await saveLogToStorage(logData);
      addLocalLog(dispatch, logData);
      dispatch(setPostScanPipeline({ ...pipeline, trackingInput: '' }));
      dispatch(resumePostScanSteps());

    } else if (pipeline.currentStep === 'Verify') {
      dispatch(handleVerifySubmit(trackingInput));
    }
  };
}

/**
 * Handles the Verify step submission logic.
 * Matches old tracking_submit.js behavior for both has-tracking and no-tracking cases.
 */
function handleVerifySubmit(trackingInput) {
  return async (dispatch, getState) => {
    const { currentOrder, generalSettings, userInfo } = getStateValues(getState);
    const pipeline = getState().scanPackScanning.postScanPipeline;
    const strictCC = generalSettings?.strict_cc;
    const trackingNum = currentOrder?.tracking_num;

    if (pipeline.verifyNoTracking) {
      // No tracking number on order — confirmation code bypass
      const userCode = userInfo?.confirmation_code;
      const { isMatch, isEmpty, allowBypass } = validateConfirmationCode(trackingInput, userCode, strictCC);
      if (isMatch || (isEmpty && allowBypass)) {
        const logData = getTrackingLog(currentOrder, 'scanpack.rfp.no_tracking_info', 'verify', trackingInput);
        logData.on_ex = 'on GPX';
        await saveLogToStorage(logData);
        addLocalLog(dispatch, logData);
        dispatch(resumePostScanSteps());
      }
      return;
    }

    // Has tracking number — compare
    if (trackingNum !== null && trackingNum !== undefined) {
      // Check retry with confirmation code bypass
      if (pipeline.scanSecondTime) {
        const userCode = userInfo?.confirmation_code;
        if (
          trackingInput === userCode ||
          (!strictCC && trackingInput === '')
        ) {
          const hasEditPermission = userInfo?.role?.add_edit_order_items;
          if (!hasEditPermission) {
            dispatch(setPostScanPipeline({
              ...pipeline,
              trackingInput: '',
              orderConfirmationPer: true
            }));
            return;
          }

          const logState = pipeline.trackingLogState || 'scanpack.rfp.no_match';
          const eventName = pipeline.isSecondOption ? 'regular' : 'verify';
          const logData = getTrackingLog(currentOrder, logState, eventName, trackingInput);
          logData.on_ex = 'on GPX';
          await saveLogToStorage(logData);
          addLocalLog(dispatch, logData);
          dispatch(setPostScanPipeline({
            ...pipeline,
            alertBoxShipping: false
          }));
          dispatch(resumePostScanSteps());
          return;
        }
      }

      let matchValue = trackingInput;
      if (pipeline.isSecondOption) {
        matchValue = trackingInput;
      } else if (trackingInput.length >= trackingNum.length) {
        const offset = trackingInput.length - trackingNum.length;
        matchValue = trackingInput.slice(offset);
      }

      if (matchValue === trackingNum) {
        const logState = pipeline.trackingLogState === 'scanpack.rfp.no_match'
          ? 'scanpack.rfp.no_match'
          : 'scanpack.rfp.verifying';
        const eventName = pipeline.isSecondOption ? 'regular' : 'verify';
        const logData = getTrackingLog(currentOrder, logState, eventName, trackingInput);
        logData.on_ex = 'on GPX';
        await saveLogToStorage(logData);
        addLocalLog(dispatch, logData);
        dispatch(resumePostScanSteps());
      } else {
        // Mismatch — show AlertBoxShippingLabel (matches old code)
        const { scanPackSettings } = getStateValues(getState);
        if (showsFailImage(scanPackSettings)) dispatch(setScanPackScanError(true, (scanPackSettings?.fail_image_time || 1) * 400));
        playErrorFeedback(getState);
        dispatch(setPostScanPipeline({
          ...pipeline,
          trackingInput: '',
          scanSecondTime: true,
          trackingLogState: 'scanpack.rfp.no_match',
          alertBoxShipping: true,
          trackState: trackingInput
        }));
      }
    }
  };
}

export function resumeAfterPackingCam() {
  return dispatch => dispatch(resumePostScanSteps());
}

/**
 * Final order completion: sound, image, API verification, email, stats, navigation.
 */
export function finalizeOrder(options = {}) {
  return async (dispatch, getState) => {
    const skipShippingLabel = !!options.skipShippingLabel;
    const { currentOrder, scanPackSettings, generalSettings } = getStateValues(getState);
    const orderId = currentOrder?.id;
    const storeInfo = currentOrder?.storeinfo;

    const {play_order_complete_sound, show_order_complete_image,order_complete_image_time, email_customer_option } = scanPackSettings

    if (play_order_complete_sound) {
      audioService.playCompleteSound();
    }

    // Show order complete image if setting enabled
    if (show_order_complete_image) {
      const imageTime = (order_complete_image_time || 1) * 400;
      dispatch(setOrderCompleteAction(true, imageTime));
    }

    resetSubmitLogThrottle();
    await submitQueuedLogsBounded(dispatch);

    // GROOV-5008: ResetSearchOrderData wipes this payload, so read it first.
    const apiOrder = getApiOrder(getState());
    dispatch(ResetSearchOrderData());

    if (orderId) {
      dispatch(FullOrderScan(orderId));
    }

    if (
      email_customer_option &&
      storeInfo?.disable_packing_cam === false &&
      orderId
    ) {
      dispatch(emailCustomer(orderId));
    }

    printCompletedOrderSlips(dispatch, getState, currentOrder, generalSettings);

    if (apiOrder) {
      dispatch(handleShippingEasyShipment(apiOrder, scanPackSettings));
      openShopifyPopup(apiOrder);
      openOrderCupPopup(apiOrder, generalSettings);
    }

    const reprintType = getReprintPrintType(scanPackSettings, generalSettings);
    if (reprintType) {
      await queueReprintPrompt(currentOrder?.increment_id, reprintType, scanPackSettings, generalSettings);
    }

    setTimeout(() => {
      getLiveStatsData();
    }, 5000);

    dispatch(setPostScanPipeline({ ...initialPostScanPipeline }));

    const navDelay = scanPackSettings?.show_order_complete_image
      ? (scanPackSettings?.order_complete_image_time || 1) * 400
      : 500;

    if (!skipShippingLabel &&
        handleMultiBoxShippingLabel(getState, currentOrder, generalSettings, scanPackSettings, navDelay)) {
      return;
    }

    const ssLabelData = getState().order?.ssLabelData;
    let labelAction = getShippingLabelAction(apiOrder, ssLabelData, generalSettings);

    // With v2 on, the module list decides whether the label screen opens and
    // where, so finalize must not open it a second time. Direct print and the
    // browser fallback are printing, not the label UI, so they still run.
    const { version } = getPostScanSteps(getState());
    if (version === 'v2' && labelAction.type === 'navigate_shipping_label') {
      labelAction = { type: 'none' };
    }

    if (!skipShippingLabel &&
        handleShippingLabelPrinting(dispatch, getState, labelAction, navDelay, currentOrder)) {
      return;
    }

    const navSource = getState().scanPackScanning.navigationSource;
    setTimeout(() => {
      dispatch(reset());
      navigateBack(getState, navSource);
    }, navDelay);
  };
}

const handleShippingLabelPrinting = (dispatch, getState, labelAction, navDelay, currentOrder) => {
  if (labelAction.type === 'none') return false;

  const { type, params } = labelAction;

  if (type === 'navigate_shipping_label') {
    setTimeout(() => {
      RootNavigation.navigate('ShippingLabel', {
        time: new Date(),
        setting: getState().user?.bothSettings?.data?.data,
        directPrintingSetting: params.directPrintingSetting,
        isolatedComponent: true,
        userRole: getState().user?.userInfo,
        basicinfo: currentOrder
      });
    }, navDelay);
    return true;
  }

  if (type === 'direct_print') {
    setTimeout(() => {
      dispatch(setPostScanPipeline({
        ...initialPostScanPipeline,
        directPrintPending: params
      }));
    }, navDelay);
    return true;
  }

  if (type === 'browser_open') {
    Linking.openURL(params.url);
  }

  return false;
};
